import React from 'react';
import { getLastScreen, getPreviousScreen } from '../Utils/screenTracker';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      recovered: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught crash in React component tree:', error, errorInfo);
    this.attemptRecovery();
  }

  attemptRecovery() {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;

    try {
      const CRASH_KEY = 'nordia_dashboard_crash_recovery';
      const now = Date.now();
      const stored = sessionStorage.getItem(CRASH_KEY);
      let crashData = { count: 0, time: 0 };
      if (stored) {
        try {
          crashData = JSON.parse(stored);
        } catch (e) {}
      }

      const isRecent = now - crashData.time < 5000;
      const count = isRecent ? crashData.count + 1 : 1;
      sessionStorage.setItem(CRASH_KEY, JSON.stringify({ count, time: now }));

      if (count <= 2) {
        const lastScreen = getLastScreen('/');
        const prevScreen = getPreviousScreen('/');
        const currentPath = window.location.pathname + window.location.search;
        const targetScreen = (lastScreen && lastScreen !== currentPath) ? lastScreen : (prevScreen || '/');

        console.warn(`[ErrorBoundary] Auto-recovering crash by loading last screen: ${targetScreen}`);
        setTimeout(() => {
          try {
            window.location.replace(targetScreen);
          } catch (e) {
            window.location.href = targetScreen;
          }
        }, 50);
      }
    } catch (e) {
      console.error('[ErrorBoundary] Recovery attempt failed:', e);
    }
  }

  handleManualReload = () => {
    const lastScreen = getLastScreen('/');
    try {
      window.location.replace(lastScreen);
    } catch (e) {
      window.location.href = lastScreen;
    }
  };

  handleGoHome = () => {
    try {
      window.location.replace('/');
    } catch (e) {
      window.location.href = '/';
    }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          padding: '20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }}>
          <div style={{
            maxWidth: '500px',
            width: '100%',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#fee2e2',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              margin: '0 auto 16px auto'
            }}>
              ⚠️
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
              Παρουσιάστηκε σφάλμα
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: 1.5 }}>
              Η εφαρμογή εντόπισε ένα μη αναμενόμενο σφάλμα. Μπορείτε να φορτώσετε ξανά την τελευταία οθόνη.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={this.handleManualReload}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 500,
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Επαναφόρτωση τελευταίας οθόνης
              </button>
              <button
                type="button"
                onClick={this.handleGoHome}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontWeight: 500,
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Αρχική οθόνη
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
